import { useMemo } from 'react';
import type { Metro } from '../../types/metro';
import { slugifyGroupName } from '../../lib/occupation-utils';
import { formatNumber } from '../../lib/utils';
import { getCoverageStyle } from './gap-coverage-style';

/**
 * Summary card answering Maria's 2026-05-26 Slack ask #1:
 * "Can these providers close the gap?" on the Providers page.
 *
 * Rendered between the ProvidersFilterBar and the table, ONLY when both
 * a metro AND an occupational group filter are active. Returns null in
 * every other case (no metro selected, no group selected, group id
 * doesn't match a row in `metro.occupationGroups`) -- keeps the page
 * layout stable when the filters don't carry enough signal.
 *
 * Layout: 3-stat row (annual shortage / produced locally / share of
 * gap covered) followed by a threshold-colored progress bar and a
 * plain-language caption. Card chrome is consistent with the
 * home-page card pattern (bg-white + shadow-card + rounded-lg +
 * border-border + p-6 + tabular-nums numbers). NationalReferenceStat
 * on Rankings uses a different chrome (bg-ice + rounded-lg) -- left
 * as-is for that page's banner style; this card chrome is the
 * larger-card pattern from the home page.
 *
 * Color thresholds follow DEC-056's honest-signaling principle:
 *   - <33%  -> rose  (severe shortage)
 *   - 33-99% -> gold (partial coverage)
 *   - >=100% -> green2 (gap covered; +N% surplus subtitle when over)
 *
 * Color tokens use the project's named palette (`rose`/`gold` /
 * `gold-dark`/`green2`) -- the same severity tokens used by the Map +
 * Rankings rely on. Not Tailwind's default scale.
 *
 * a11y:
 *   - aria-labelledby points to the eyebrow id so screen readers
 *     announce a meaningful section name.
 *   - Stats render as a semantic <dl>/<dt>/<dd> trio.
 *   - The progress bar uses role="progressbar" with
 *     aria-valuenow/min/max + an aria-label describing the coverage.
 *   - The inner colored fill carries aria-hidden -- the parent
 *     progressbar carries all semantics.
 *   - Fade-in uses a CSS transition that's neutralized via
 *     motion-reduce: utility classes.
 */
interface GapCoverageCardProps {
  readonly metro: Metro;
  readonly groupId: string;
  /**
   * Count of providers currently matching the filter in the table
   * below. Surfaces Maria's 2026-05-29 bug report -- the metro-level
   * alignment number ("400 credentials produced locally") can disagree
   * with the institution-level providers list ("0 institutions") for
   * the same metro+group filter, because the two come from different
   * xlsx sheets with different methodologies (CIP->SOC crosswalk vs
   * direct CIP attribution per IPEDS). We surface both numbers + a
   * methodology note instead of picking a side -- Georgetown will
   * reconcile to a canonical source in the production data release.
   *
   * `null`/`undefined` = providers count not yet known (e.g. the
   * /api/providers fetch hasn't resolved on this render). In that
   * case the methodology note hides until the count is known, rather
   * than briefly showing a misleading "0 providers" zero-default.
   */
  readonly providersInFilterCount?: number | null;
}

const EYEBROW_ID = 'gap-coverage-eyebrow';

export function GapCoverageCard({
  metro,
  groupId,
  // 2026-07-02 (V2 feedback): the methodology-note block that consumed
  // this prop was retired. Kept on the signature so
  // ProvidersTable.tsx (and any other caller) can keep passing it
  // without a change; underscore prefix silences the unused-vars lint
  // rule until we either bring the note back or clean up the prop
  // entirely in a follow-up.
  providersInFilterCount: _providersInFilterCount,
}: GapCoverageCardProps) {
  const selectedGroup = useMemo(() => {
    if (!metro || !groupId) return undefined;
    const groups = metro.occupationGroups;
    if (!groups || groups.length === 0) return undefined;
    return groups.find((g) => slugifyGroupName(g.name) === groupId);
  }, [metro, groupId]);

  if (!selectedGroup) return null;

  const shortage = selectedGroup.shortage;
  const producedLoc = selectedGroup.awardsSb;
  const totalDemand = selectedGroup.numOpeningsTotal;
  const coveragePct =
    totalDemand > 0 ? Math.round((producedLoc / totalDemand) * 100) : 0;
  const isSurplus = coveragePct > 100;
  const style = getCoverageStyle(coveragePct);

  // Bar fill width caps at 100% so a surplus doesn't visually
  // overflow the track -- the "+N% surplus" subtitle carries the
  // numeric truth instead.
  const barWidthPct = Math.min(coveragePct, 100);

  return (
    <section
      aria-labelledby={EYEBROW_ID}
      className="mb-4 rounded-lg border border-border bg-white p-6 shadow-card opacity-100 transition-opacity duration-200 motion-reduce:transition-none"
    >
      <div
        id={EYEBROW_ID}
        className="mb-4 text-eyebrow font-semibold uppercase text-navy"
      >
        Gap coverage &mdash; {metro.name} &middot; {selectedGroup.name}
      </div>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <dt className="text-sm text-text-4">Annual credential shortage</dt>
          <dd className="mt-1 text-4xl font-bold tabular-nums text-rose">
            {formatNumber(shortage)}
          </dd>
        </div>
        <div>
          <dt className="text-sm text-text-4">Estimated credentials produced locally</dt>
          <dd className="mt-1 text-4xl font-bold tabular-nums text-navy">
            {formatNumber(producedLoc)}
          </dd>
        </div>
        <div>
          <dt className="text-sm text-text-4">Share of gap covered</dt>
          <dd
            className={`mt-1 text-4xl font-bold tabular-nums ${style.textClass}`}
          >
            {coveragePct}%
          </dd>
        </div>
      </dl>

      <div className="mt-6">
        <div
          role="progressbar"
          aria-valuenow={Math.min(coveragePct, 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${coveragePct}% of demand covered by local providers`}
          className="h-2 w-full overflow-hidden rounded-full bg-ice"
        >
          <div
            aria-hidden="true"
            className={`h-full rounded-full transition-[width] duration-200 motion-reduce:transition-none ${style.barClass}`}
            style={{ width: `${barWidthPct}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-text-4">
          <span>0</span>
          <span className={style.textClass}>{style.label}</span>
          <span>Gap fully covered (100%)</span>
        </div>
      </div>

      <p className="mt-4 text-sm italic text-text-3">
        {isSurplus
          ? `At current production, local providers exceed annual demand for ${selectedGroup.name.toLowerCase()} by ${coveragePct - 100}%.`
          : `At current production, local providers cover ${coveragePct}% of annual demand for ${selectedGroup.name.toLowerCase()}.`}
      </p>

      {/* 2026-07-02 (V2 feedback): the "Methodology note:" block that
          used to render at the bottom of this card was removed per
          Georgetown reviewers — the same reconciliation caveat is now
          carried by the shared TableSourceNote under the providers
          table, so repeating it here was redundant. Kept
          `providersInFilterCount` on the props signature so callers
          don't have to change; the count still drives the
          coverage-percent calculation above. */}
    </section>
  );
}
